<template>
  <v-card class="h-100 py-6" rounded="lg" elevation="0">
    <div class="px-6" v-if="widgetData.loading">
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" />
      <v-skeleton-loader
        type="list-item-two-line@3,divider,list-item-two-line"
        class="h-100 bg-transparent"
      />
    </div>

    <div v-else-if="widgetData.data" class="px-6 pb-4">
      <div class="d-flex align-center justify-space-between mb-4">
        <div class="subtitle2">
          {{ $t("mspDashboard.widgets.usersAndDevicesTrend.title") }}
        </div>
        <div class="d-flex align-center">
          <div
            @click="exportCsv"
            class="export-csv-btn body2 coro-link item-clickable d-flex align-center mr-4"
          >
            <v-icon icon="$export" color="link" size="16" />
            <span>{{ $t("general.exportCsv") }}</span>
          </div>
          <v-select
            v-model="selectedRange"
            :items="presets"
            density="compact"
            variant="outlined"
            hide-details
            rounded
            style="max-width: max-content"
            class="rounded-lg"
            item-title="label"
            item-value="value"
            return-object
            data-testid="top-delta-widget-range-select"
            @click="sendGtmEvent('Time selector clicked')"
          ></v-select>
        </div>
      </div>
      <div class="d-flex align-center mb-2">
        <div
          class="caption mr-3 text-green-dark"
          :class="{ 'text-red-dark': widgetData.data.usersDelta < 0 }"
          v-if="widgetData.data.usersDelta !== 0"
        >
          <span v-if="widgetData.data.usersDelta > 0">+</span>
          <span>{{ widgetData.data.usersDelta }}</span>
          <span class="ml-1 text-lowercase">
            {{ $t("mspDashboard.widgets.usersAndDevicesTrend.users") }}
          </span>
          <v-icon
            icon="$sortArrowDown"
            size="14"
            class="sort-arrow icon-reverse"
            :class="{ 'sort-arrow--ascending': widgetData.data.usersDelta > 0 }"
          />
        </div>
        <div
          class="caption text-green-dark"
          :class="{ 'text-red-dark': widgetData.data.devicesDelta < 0 }"
          v-if="widgetData.data.devicesDelta !== 0"
        >
          <span v-if="widgetData.data.devicesDelta > 0">+</span>
          <span>{{ widgetData.data.devicesDelta }}</span>
          <span class="ml-1 text-lowercase">
            {{ $t("mspDashboard.widgets.usersAndDevicesTrend.devices") }}
          </span>
          <v-icon
            icon="$sortArrowDown"
            size="14"
            class="sort-arrow"
            :class="{ 'sort-arrow--ascending': widgetData.data.devicesDelta > 0 }"
          />
        </div>
      </div>
      <div class="chart-content">
        <LineChart :data="chartData" :options="chartOptions" />
      </div>
    </div>
    <div
      v-else
      class="h-100 d-flex align-center justify-center flex-column px-6"
      data-testid="widget-error-state"
    >
      <v-icon icon="$alertTriangle" size="80"></v-icon>
      <span class="subtitle1 mb-4 text-center">
        {{ $t("mspDashboard.widgets.usersAndDevicesTrend.error") }}
      </span>
      <div
        class="coro-link"
        @click="$emit('retry-fetch-data', selectedRange.value)"
        data-testid="users-and-devices-trend-widget-retry"
      >
        {{ $t("general.retry") }}
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type PropType, computed, ref, watch } from "vue";
import type { DataValue } from "@/types.ts";
import {
  useMspDashboardStore,
  type UsersAndDevicesTrend,
  type UsersAndDevicesTrendChartItem,
} from "@/_store/msp/msp-dashboard.module.ts";
import LineChart, { defaultLineChartOptions } from "@/components/LineChart.vue";
import { type ChartData, type ChartOptions } from "chart.js";
import moment from "moment/moment";
import { useI18n } from "vue-i18n";
import { trackGtmEvent } from "@/_helpers/analytics";
import { MspExportAction } from "@/constants/msp-exports.ts";
import {
  MspDashboardRange,
  mspDashboardRangeLabelKeys,
  type MspDashboardRangePreset,
} from "@/constants/msp.ts";
import { ModalWidth } from "@/constants/modals.ts";
import MspExportUsersAndDevicesTrendModal from "@/components/modals/msp/MspExportUsersAndDevicesTrendModal.vue";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";

type UsersAndDevicesTrendFilterPeriod = "week" | "month";

export default defineComponent({
  components: { LineChart },
  props: {
    widgetData: {
      type: Object as PropType<DataValue<UsersAndDevicesTrend>>,
      required: true,
    },
  },
  emits: ["retry-fetch-data"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const dialogsStore = useDialogsStore();
    const mspDashboardStore = useMspDashboardStore();
    const presets = computed<MspDashboardRangePreset[]>(() =>
      Object.values(MspDashboardRange).map((value) => ({
        label: i18n.t(mspDashboardRangeLabelKeys[value]),
        value,
      }))
    );
    const selectedRange = ref<MspDashboardRangePreset>(presets.value[0]);

    /**
     * Extracted as a separate computed so both `chartData` and `chartOptions` can
     * consume the same filtered list without duplicating the period-bucketing logic.
     * The active date range preset determines which granularity is applied:
     * 7/30 days → raw daily points, 90 days → last point per ISO week, 6/13 months → last point per month.
     */
    const filteredChartItems = computed<UsersAndDevicesTrendChartItem[]>(() => {
      const items = props.widgetData?.data?.items || [];

      switch (selectedRange.value.value) {
        case MspDashboardRange.LAST_90_DAYS:
          return filterByPeriod(items, "week");
        case MspDashboardRange.LAST_6_MONTHS:
        case MspDashboardRange.LAST_13_MONTHS:
          return filterByPeriod(items, "month");
        default:
          return [...items];
      }
    });

    /**
     * Dynamically pins the Y-axis lower bound to prevent Chart.js from
     * centering the scale symmetrically around 0 when all data points are zero.
     *
     * Chart.js default behaviour: if every value is 0, it builds a symmetric
     * range (e.g. -1 → 1), placing the flat line in the middle of the chart
     * instead of at the bottom — which looks broken to the user.
     *
     * Strategy:
     * - Any value equals 0   → `min: 0`  — pins the axis bottom edge at zero,
     *   keeping the line at the bottom and blocking the negative phantom range.
     * - All values positive  → `min: undefined` — lets Chart.js auto-scale
     *   tightly around the real data so the chart is not artificially stretched
     *   down to 0 when e.g. the data lives in the 100–200 range.
     * - Empty dataset        → `min: 0`  — safe fallback; nothing to display.
     */
    const yAxisMin = computed<number | undefined>(() => {
      const allValues = filteredChartItems.value.flatMap((item) => [item.users, item.devices]);
      if (!allValues.length) return 0;
      return Math.min(...allValues) === 0 ? 0 : undefined;
    });

    const chartOptions = computed(() => {
      const options: ChartOptions<"line"> = {
        ...defaultLineChartOptions,
        scales: {
          ...defaultLineChartOptions.scales,
          y: {
            beginAtZero: false,
            min: yAxisMin.value,
            grid: {
              display: true,
            },
            ticks: {
              precision: 0,
            },
          },
        },
      };
      return options;
    });

    const chartData = computed(() => {
      const result: ChartData<"line"> = {
        labels: [],
        datasets: [
          {
            label: i18n.t("mspDashboard.widgets.usersAndDevicesTrend.devices"),
            backgroundColor: "#6200EE",
            borderColor: "#6200EE",
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: "white",
            pointHoverBorderColor: "#6200EE",
            data: [],
          },
          {
            label: i18n.t("mspDashboard.widgets.usersAndDevicesTrend.users"),
            backgroundColor: "#26A69A",
            borderColor: "#26A69A",
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: "white",
            pointHoverBorderColor: "#26A69A",
            data: [],
          },
        ],
      };

      filteredChartItems.value.forEach((item: UsersAndDevicesTrendChartItem) => {
        result?.labels?.push(moment(item.date).format("MMM D"));
        result.datasets[0].data.push(item.devices);
        result.datasets[1].data.push(item.users);
      });

      return result;
    });

    const sendGtmEvent = (event: string) => {
      trackGtmEvent(
        GtmAction.MSP_DASHBOARD_WIDGET,
        `${GTMParams.TOTAL_PROTECTED_USERS_AND_DEVICES} - ${event}`
      );
    };

    const filterByPeriod = (
      data: UsersAndDevicesTrendChartItem[],
      period: UsersAndDevicesTrendFilterPeriod = "week"
    ): UsersAndDevicesTrendChartItem[] => {
      return Object.values(
        data.reduce(
          (acc: Record<string, UsersAndDevicesTrendChartItem>, item) => {
            const date = moment(item.date);
            let key: string = "";

            if (period === "week") {
              key = `${date.isoWeekYear()}-${date.isoWeek()}`;
            } else if (period === "month") {
              key = `${date.year()}-${date.month()}`;
            }

            if (!acc[key] || item.date > acc[key].date) {
              acc[key] = item;
            }

            return acc;
          },
          {} as Record<string, UsersAndDevicesTrendChartItem>
        )
      );
    };

    const exportCsv = () => {
      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          action: MspExportAction.EXPORT_USERS_AND_DEVICES_TREND,
          callback: async (data) => {
            await mspDashboardStore.exportUsersAndDevicesTrend({
              fromTimestamp: moment(data.date.value).utc().valueOf(),
              exportFields: data.type,
            });
          },
          component: MspExportUsersAndDevicesTrendModal,
          width: ModalWidth.LARGE,
          disable: false,
        }),
        disablePersistentAnimation: true,
      });

      sendGtmEvent("Export CSV");
    };

    // Watch for changes in selected range and emit event to fetch new data
    watch(
      selectedRange,
      (newRange) => {
        emit("retry-fetch-data", newRange.value);
      },
      { deep: true }
    );

    return {
      chartOptions,
      chartData,
      selectedRange,
      presets,
      exportCsv,
      filterByPeriod,
      sendGtmEvent,
    };
  },
});
</script>
<style lang="scss" scoped>
.chart-content {
  height: 200px;
}

.sort-arrow {
  &--ascending:before {
    color: rgb(var(--v-theme-green-dark)) !important;
  }
  &--ascending {
    transform: rotate(180deg);
  }
}

.sort-arrow:before {
  color: rgb(var(--v-theme-red-dark));
}
</style>
